import type { MouseEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type React from 'react';
import classNames from 'classnames';
import debounce from 'debounce';
import styles from './FaqNavigation.module.scss';
import type { Category } from '../../../types';
import { window } from '../../../../../../js/globals';
import scrollToElement from '../../../../../../js/util/scrollToElement';
import { useInViewContext } from '../../../Context/InViewContextProvider';
import slugify from '../../../../../../js/util/slugify';

interface FaqNavigationProps {
    categories: Category[];
    faqUri: string;
}

const FaqNavigation: React.FC<FaqNavigationProps> = ({ categories, faqUri }) => {
    const [inView] = useInViewContext();

    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const scrollTo = useCallback((categoryId: string | undefined) => {
        if (window && categoryId) {
            history.replaceState({}, '', `${faqUri}#${categoryId}`);
            scrollToElement(categoryId);
        }
    }, [faqUri]);

    const onClick = (e: MouseEvent<HTMLButtonElement>) => {
        const categoryID = e.currentTarget.dataset.category;
        scrollTo(categoryID);
    };

    const firstActive = categories.find((category) => inView[category.id]);
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        const scrollFaqNav = debounce(() => {
            const element = buttonRef.current;

            if (!(element instanceof HTMLElement)) {
                return;
            }

            if (wrapperRef.current && 'scroll' in wrapperRef.current) {
                wrapperRef.current?.scroll({
                    behavior: 'smooth',
                    left: element.offsetLeft - 18,
                });
            }
        }, 50);

        window?.addEventListener('scroll', scrollFaqNav);
        return () => window?.removeEventListener('scroll', scrollFaqNav);
    }, []);

    return (
        <div data-go-class='FaqNavigation__header' className={styles.FaqNavigation__section}>
            <div className={styles.FaqNavigation}>
                <div ref={wrapperRef} className={classNames('container u-scrollable-x', styles.FaqNavigation__wrapper)}>
                    {categories.map((category) => (
                        <button
                            ref={firstActive && Number(firstActive.id) === category.id ? buttonRef : undefined}
                            key={category.name}
                            data-id={category.id}
                            type="button"
                            data-category={slugify(category.name)}
                            className={classNames({
                                [styles.FaqNavigation__active as string]: firstActive && Number(firstActive.id) === category.id,
                            }, styles.FaqNavigation__link)}
                            onClick={onClick}
                        >
                            {category.name}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};
export default FaqNavigation;
